import { Line, LineConfig, PlotEvent } from '@ant-design/charts';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_TOTAL_USERS_COUNT,
  GET_USER_STATISTICS_WITH_ROLES,
} from '@frontend/data-access';
import { getLinkLabel } from '@frontend/shared/util';
import { Card, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import {
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

const { RangePicker } = DatePicker;

const TotalUsers = () => {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');

  const { data: totalUsers } = useQuery<{
    totalUserCount: number;
  }>(GET_TOTAL_USERS_COUNT);
  const endDate = dayjs();
  const startDate = endDate.subtract(3, 'month');
  const [date, setDate] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);

  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  const [getUserStatistics, { data, loading }] = useLazyQuery<{
    userStatisticsWithRoles: {
      date: string;
      values: {
        role: string;
        count: number;
      }[];
    }[];
  }>(GET_USER_STATISTICS_WITH_ROLES);

  const getLabel = useCallback(
    (value: string) => {
      switch (value) {
        case 'INTRAPRENEUR':
          return t('intrapreneur');
        case 'EXTERNAL_USER':
          return t('external');
        case 'ADMIN':
          return t('admin');
        default:
          return getLinkLabel(value?.toLowerCase());
      }
    },
    [t]
  );

  const convertJsonStructure = useMemo(() => {
    const userStatistics = data?.userStatisticsWithRoles;

    if (!userStatistics) return [];

    return userStatistics
      .flatMap(({ date, values }) => {
        const totalCount = values.reduce((sum, { count }) => sum + count, 0);

        const individualEntries = values.map(({ role, count }) => ({
          date,
          role: getLabel(role),
          count,
        }));

        // Prepend the aggregated "All" entry
        return [{ date, role: 'All', count: totalCount }, ...individualEntries];
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data?.userStatisticsWithRoles, getLabel]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const defaultSelectedItems = useMemo(() => {
    if (convertJsonStructure.length > 0) {
      return convertJsonStructure.map((item) => item.role);
    }
    return [];
  }, [convertJsonStructure]);

  useEffect(() => {
    setSelectedItems(defaultSelectedItems);
  }, [defaultSelectedItems]);

  const config = useMemo<LineConfig & RefAttributes<unknown>>(
    () => ({
      data: convertJsonStructure,
      loading,
      xField: 'date',
      yField: 'count',
      colorField: 'role',
      seriesField: 'role',
      yAxis: {
        label: {
          style: {
            fontFamily: 'Lufga',
          },
        },
      },
      xAxis: {
        label: {
          style: {
            fontFamily: 'Lufga',
          },
        },
        x: {
          label: {
            style: {
              fontFamily: 'Lufga',
            },
          },
          labelFormatter: (v) => {
            const monthIndex = parseInt(v.split('-')[1], 10) - 1;
            return monthNames[monthIndex];
          },
        },
      },
      paddingTop: 32,
      legend: {
        color: {
          position: 'bottom',
          autoWrap: true,
          itemMarker: 'square',
          style: {
            fontSize: 14,
            fontFamily: 'Lufga',
          },
          layout: {
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      },
      onReady: ({ chart }) => {
        chart.on('legend:filter', (e: PlotEvent) => {
          if (e?.data?.values) {
            const values = e?.data?.values as Array<string>;
            setSelectedItems([...values]);
          }
        });
      },
      interactions: [
        {
          type: 'legend-filter',
        },
      ],
      animation: {
        appear: {
          animation: 'path-in',
          duration: 100,
        },
      },
      label: {
        text: 'count',
        position: 'top',
        textAlign: 'center',
        dy: -10,
        style: {
          fontFamily: 'Lufga',
        },
      },
    }),
    [convertJsonStructure, loading, monthNames]
  );

  const visibleTotal = useMemo(() => {
    if (selectedItems.includes('All')) {
      return convertJsonStructure
        .filter((item) => item.role === 'All')
        .reduce((acc, curr) => acc + curr.count, 0);
    } else {
      return convertJsonStructure
        .filter(
          (item) => item.role !== 'All' && selectedItems.includes(item.role)
        )
        .reduce((acc, curr) => acc + curr.count, 0);
    }
  }, [convertJsonStructure, selectedItems]);

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDate([dateStrings[0], dateStrings[1]]);
    }
  };

  useEffect(() => {
    if (date.length) {
      getUserStatistics({
        variables: {
          input: {
            startDate: date[0],
            endDate: date[1],
          },
        },
      });
    }
  }, [date, getUserStatistics]);

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="flex items-center flex-wrap gap-1">
            <span>{t('total_during_period')}</span>
            <span className="text-gray-700 font-medium">
              / {t('total_all_time')}
            </span>
          </h2>
          <div>
            <RangePicker
              onChange={handleChange}
              defaultValue={[dayjs(date[0]), dayjs(date[1])]}
              style={{
                borderRadius: 20,
              }}
            />
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-1 my-4">
          <span className="text-gray-900 text-3xl">{visibleTotal}</span>
          <span className="text-gray-700 text-2xl">
            / {totalUsers?.totalUserCount?.toLocaleString()}
          </span>
        </div>
        <Line {...config} />
      </div>
    </Card>
  );
};

export default TotalUsers;
